/**
 * PhonemeDetailSheet — bottom sheet with one sound's full detail:
 * label, example words, learner stats (avg / attempts / last practiced),
 * contrastive difficulty + note + teaching tips when present, and the
 * "Practice this sound" primary action (opens the pronunciation coach
 * pre-targeted on this sound — the screen passes ipaSymbol as
 * focusPhoneme on session creation).
 *
 * Wraps the shared BottomSheetModal (safe-area-aware card).
 *
 * @see docs/specs/mobile/sound-map.mobile.md
 */

import React from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Ionicons from '../ui/Ionicons';
import { BottomSheetModal } from '../ui/BottomSheetModal';
import { colors, FS_XS, FS_SM, FS_MD } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import type { SoundMapPhoneme } from '../../generated/api/http-contracts';
import {
  showsExpectedHardBadge,
  translateStateLabel,
  type PhonemeState,
} from './soundMapText';

const FS_SYMBOL = 34;

interface PhonemeDetailSheetProps {
  phoneme: SoundMapPhoneme;
  visible: boolean;
  onClose: () => void;
  /** Start a pronunciation-coach session (spinner shown while creating). */
  onPractice: () => void;
  practiceLoading: boolean;
  /** Render inline without the Modal wrapper — for Storybook. */
  embedded?: boolean;
}

/** State pill colors — same semantic mapping as the chips. */
function useStatePillColors(state: PhonemeState) {
  const { theme } = useDisplayTheme();
  switch (state) {
    case 'mastered': return { bg: theme.successBg, text: theme.success };
    case 'learning': return { bg: theme.warningBg, text: theme.warning };
    case 'weak': return { bg: theme.errorBg, text: theme.error };
    case 'untried': return { bg: theme.inputBg, text: theme.textSecondary };
  }
}

function StatRow({ label, value }: { label: string; value: string }) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  return (
    <View style={styles.statRow}>
      <Text
        allowFontScaling={false}
        style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}
      >
        {label}
      </Text>
      <Text
        allowFontScaling={false}
        style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
      >
        {value}
      </Text>
    </View>
  );
}

export function PhonemeDetailSheet({
  phoneme,
  visible,
  onClose,
  onPractice,
  practiceLoading,
  embedded = false,
}: PhonemeDetailSheetProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const pillColors = useStatePillColors(phoneme.state);

  const tried = phoneme.state !== 'untried';
  const hasTips = (phoneme.teachingTips?.length ?? 0) > 0;

  return (
    <BottomSheetModal
      visible={visible}
      onRequestClose={onClose}
      embedded={embedded}
      testID="sound-map-detail"
    >
      <ScrollView showsVerticalScrollIndicator={false}>
        {/* ── Header: symbol + label + close ── */}
        <View style={styles.headerRow}>
          <View style={styles.headerLeft}>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SYMBOL * fontScale, fontWeight: '700', color: theme.text, ...ffs }}
            >
              {phoneme.ipaSymbol}
            </Text>
            <View style={styles.headerText}>
              <Text
                allowFontScaling={false}
                accessibilityRole="header"
                style={{ fontSize: FS_MD * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
              >
                {phoneme.label}
              </Text>
              <View style={[styles.statePill, { backgroundColor: pillColors.bg }]}>
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_XS * fontScale, fontWeight: '600', color: pillColors.text, ...ffs }}
                >
                  {translateStateLabel(t, phoneme.state)}
                </Text>
              </View>
            </View>
          </View>
          <TouchableOpacity
            testID="sound-map-detail-close"
            accessibilityRole="button"
            accessibilityLabel={t('soundMap.detail.closeA11y')}
            onPress={onClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name="close" size={24 * fontScale} color={theme.textSecondary} />
          </TouchableOpacity>
        </View>

        {/* ── Example words ── */}
        {phoneme.exampleWords.length > 0 && (
          <View style={styles.section}>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_XS * fontScale, fontWeight: '600', color: theme.textSecondary, ...ffs }}
            >
              {t('soundMap.detail.exampleWordsTitle')}
            </Text>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_MD * fontScale, color: theme.text, marginTop: 4, ...ffs }}
            >
              {phoneme.exampleWords.join(', ')}
            </Text>
          </View>
        )}

        {/* ── Learner stats ── */}
        <View style={[styles.statsCard, { backgroundColor: theme.inputBg }]}>
          {tried ? (
            <>
              {phoneme.avgScore !== null && (
                <StatRow
                  label={t('soundMap.detail.statsAvgScore')}
                  value={t('soundMap.chip.scoreValue', { score: phoneme.avgScore })}
                />
              )}
              <StatRow
                label={t('soundMap.detail.statsAttempts')}
                value={String(phoneme.attempts)}
              />
              {phoneme.lastPracticedAt !== null && (
                <StatRow
                  label={t('soundMap.detail.statsLastPracticed')}
                  value={new Date(phoneme.lastPracticedAt).toLocaleDateString()}
                />
              )}
            </>
          ) : (
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}
            >
              {t('soundMap.detail.notPracticedYet')}
            </Text>
          )}
        </View>

        {/* ── Contrastive difficulty ── */}
        {phoneme.difficulty !== null && (
          <View style={styles.section}>
            <View style={styles.statRow}>
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}
              >
                {t('soundMap.detail.difficultyLabel')}
              </Text>
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
              >
                {t('soundMap.detail.difficultyValue', { difficulty: phoneme.difficulty })}
              </Text>
            </View>
            {showsExpectedHardBadge(phoneme) && (
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_XS * fontScale, color: theme.warning, marginTop: 4, ...ffs }}
              >
                {t('soundMap.detail.expectedHard')}
              </Text>
            )}
            {!!phoneme.contrastiveNote && (
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, color: theme.text, marginTop: 6, ...ffs }}
              >
                {phoneme.contrastiveNote}
              </Text>
            )}
          </View>
        )}

        {/* ── Teaching tips ── */}
        {hasTips && (
          <View style={styles.section}>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_XS * fontScale, fontWeight: '600', color: theme.textSecondary, ...ffs }}
            >
              {t('soundMap.detail.tipsTitle')}
            </Text>
            {phoneme.teachingTips?.map((tip, index) => (
              <View key={index} style={styles.tipRow}>
                <Text
                  allowFontScaling={false}
                  accessible={false}
                  importantForAccessibility="no"
                  style={{ fontSize: FS_SM * fontScale, color: theme.primary, ...ffs }}
                >
                  •
                </Text>
                <Text
                  allowFontScaling={false}
                  style={{ flex: 1, fontSize: FS_SM * fontScale, color: theme.text, marginLeft: 8, ...ffs }}
                >
                  {tip}
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* ── Primary action ── */}
        <TouchableOpacity
          testID="sound-map-detail-practice"
          accessibilityRole="button"
          accessibilityLabel={t('soundMap.detail.practiceButton')}
          accessibilityState={{ disabled: practiceLoading }}
          onPress={onPractice}
          disabled={practiceLoading}
          activeOpacity={0.8}
          style={[styles.practiceButton, { backgroundColor: colors.primary, opacity: practiceLoading ? 0.7 : 1 }]}
        >
          {practiceLoading ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_MD * fontScale, fontWeight: '700', color: '#FFFFFF', ...ffs }}
            >
              {t('soundMap.detail.practiceButton')}
            </Text>
          )}
        </TouchableOpacity>
      </ScrollView>
    </BottomSheetModal>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 12,
  },
  headerText: {
    flex: 1,
    marginLeft: 14,
  },
  statePill: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    marginTop: 6,
  },
  section: {
    marginTop: 16,
  },
  statsCard: {
    marginTop: 16,
    borderRadius: 12,
    padding: 12,
    gap: 8,
  },
  statRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tipRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 6,
  },
  practiceButton: {
    marginTop: 24,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
});
